import { useMemo, useState } from 'react'
import {
  FaArrowLeft,
  FaBullhorn,
  FaChartLine,
  FaDollarSign,
  FaClock,
  FaCompass,
  FaEye,
  FaHeart,
  FaImage,
  FaMapMarkerAlt,
  FaRoute,
  FaSatelliteDish,
  FaSearch,
  FaShieldAlt,
  FaSignOutAlt,
  FaStar,
  FaUser,
  FaUserFriends,
  FaUsers
} from 'react-icons/fa'
import { useQuery } from '@tanstack/react-query'

import { useAuth } from 'contexts/auth.context'
import { useUser } from 'contexts/user.context'

import api from 'services/api'

import * as S from './style'

type DashboardData = {
  periodDays: number
  summary: {
    totalUsers: number
    newUsers: number
    usersChange: number
    totalItineraries: number
    publishedItineraries: number
    plannedItineraries: number
    newItineraries: number
    itinerariesChange: number
    uniqueDestinations: number
    totalCityEntries: number
    totalRatings: number
    averageRating: number
    totalFavorites: number
    totalConnections: number
  }
  growth: Array<{
    month: string
    label: string
    users: number
    itineraries: number
  }>
  content: {
    published: number
    planned: number
  }
  engagement: {
    ratings: number
    favorites: number
    connections: number
    averageRating: number
  }
  strategy: {
    publishingUsers: number
    publisherRate: number
    averageCitiesPerItinerary: number
    inspirations: number
    inspirationSources: number
    inspirationSourceRate: number
    plannedCreated: number
    realizedTransitions: number
    plannedToRealizedRate: number
  }
  audience: {
    pageViews: number
    sessions: number
    activeUsers: number
    analyticsSince?: string | null
  }
  retention: {
    day7: { eligible: number; retained: number; rate: number }
    day30: { eligible: number; retained: number; rate: number }
  }
  funnel: {
    discoveryViews: number
    itineraryViews: number
    commercialClicks: number
    discoveryToItineraryRate: number
    itineraryToCommercialRate: number
  }
  searches: Array<{
    term: string
    searches: number
    noResult: number
  }>
  demandGaps: Array<{
    term: string
    searches: number
    contentCount: number
  }>
  commercial: Array<{
    partner: string
    impressions: number
    clicks: number
    ctr: number
  }>
  audienceBreakdown: {
    topPages: Array<{ path: string; views: number }>
    trafficSources: Array<{ source: string; views: number }>
    devices: Array<{ device: string; views: number }>
    countries: Array<{ country: string; views: number }>
  }
  moderation: {
    suspended: number
    banned: number
    verified: number
  }
  topDestinations: Array<{
    name: string
    itineraries: number
  }>
  recentActivity: Array<{
    id: string
    type: 'user' | 'itinerary'
    title: string
    detail: string
    createdAt: string
  }>
}

const Admin = () => {
  const { user } = useUser()
  const { signOut } = useAuth()
  const [periodDays, setPeriodDays] = useState(30)

  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-dashboard', periodDays],
    queryFn: async () => {
      const response = await api.get<DashboardData>(
        `/admin/dashboard?days=${periodDays}`
      )
      return response.data
    },
    staleTime: 60 * 1000
  })

  const maxGrowth = useMemo(() => {
    if (!data?.growth?.length) return 1
    return Math.max(
      1,
      ...data.growth.flatMap((item) => [item.users, item.itineraries])
    )
  }, [data?.growth])

  const totalContent =
    (data?.content.published ?? 0) + (data?.content.planned ?? 0)
  const publishedPercent = totalContent
    ? Math.round(((data?.content.published ?? 0) / totalContent) * 100)
    : 0

  const formatNumber = (value?: number) =>
    new Intl.NumberFormat('pt-BR').format(value ?? 0)

  const changeLabel = (value?: number) => {
    const amount = value ?? 0
    return `${amount > 0 ? '+' : ''}${amount}%`
  }

  const relativeTime = (value: string) => {
    const created = new Date(value).getTime()
    const diff = Math.max(0, Date.now() - created)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(hours / 24)

    if (hours < 1) return 'há menos de 1 hora'
    if (hours < 24) return `há ${hours}h`
    if (days === 1) return 'há 1 dia'
    return `há ${days} dias`
  }

  return (
    <S.Page>
      <S.Sidebar>
        <S.BrandBlock>
          <S.BrandLink to="/">
            <S.Logo src="/images/header-logo.png" alt="RedSterna" />
          </S.BrandLink>
          <S.AdminBadge>
            <FaShieldAlt />
            Admin
          </S.AdminBadge>
        </S.BrandBlock>

        <S.SidebarBody>
          <S.SidebarLabel>Gestão</S.SidebarLabel>
          <S.SidebarNav>
            <S.SidebarNavLink to="/admin" $active>
              <FaChartLine />
              Dashboard
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/usuarios">
              <FaUsers />
              Usuários
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/localidades">
              <FaMapMarkerAlt />
              Localidades
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/roteiros">
              <FaRoute />
              Roteiros
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/moderacao">
              <FaImage />
              Moderação
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/anuncios">
              <FaBullhorn />
              Anúncios
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/radar">
              <FaSatelliteDish />
              Radar
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/comissoes">
              <FaDollarSign />
              Comissões
            </S.SidebarNavLink>
          </S.SidebarNav>
        </S.SidebarBody>

        <S.SidebarFooter>
          <S.SidebarLink to="/minha-conta">
            <FaUser />
            Meu perfil
          </S.SidebarLink>
          <S.SidebarLink to="/">
            <FaArrowLeft />
            Voltar ao site
          </S.SidebarLink>
        </S.SidebarFooter>
      </S.Sidebar>

      <S.Main>
        <S.Topbar>
          <div>
            <S.TopbarEyebrow>RedSterna Admin</S.TopbarEyebrow>
            <S.TopbarTitle>Dashboard</S.TopbarTitle>
          </div>

          <S.UserArea>
            <S.UserAvatar>
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={fullName || 'Administrador'}
                />
              ) : (
                <FaUser />
              )}
            </S.UserAvatar>
            <S.UserInfo>
              <strong>{fullName || 'Administrador'}</strong>
              <span>{user?.email}</span>
            </S.UserInfo>
            <S.AdminChip>Admin</S.AdminChip>
            <S.SignOutButton type="button" onClick={signOut} aria-label="Sair">
              <FaSignOutAlt />
            </S.SignOutButton>
          </S.UserArea>
        </S.Topbar>

        <S.Content>
          <S.DashboardHeader>
            <div>
              <h1>Visão geral da RedSterna</h1>
              <p>
                Acompanhe crescimento, conteúdo e sinais de engajamento da
                comunidade.
              </p>
            </div>
            <S.PeriodSelect
              value={periodDays}
              onChange={(event) => setPeriodDays(Number(event.target.value))}
            >
              <option value={7}>Últimos 7 dias</option>
              <option value={30}>Últimos 30 dias</option>
              <option value={90}>Últimos 90 dias</option>
            </S.PeriodSelect>
          </S.DashboardHeader>

          {isLoading && (
            <S.StateCard>Carregando indicadores do dashboard...</S.StateCard>
          )}

          {isError && (
            <S.StateCard $error>
              Não foi possível carregar os indicadores agora.
            </S.StateCard>
          )}

          {data && (
            <>
              <S.SummaryGrid>
                <S.SummaryCard>
                  <S.SummaryTop>
                    <S.IconBox $tone="blue">
                      <FaUsers />
                    </S.IconBox>
                    <S.Change $positive={data.summary.usersChange >= 0}>
                      {changeLabel(data.summary.usersChange)}
                    </S.Change>
                  </S.SummaryTop>
                  <strong>{formatNumber(data.summary.totalUsers)}</strong>
                  <span>Usuários cadastrados</span>
                  <small>
                    {formatNumber(data.summary.newUsers)} novos no período
                  </small>
                </S.SummaryCard>

                <S.SummaryCard>
                  <S.SummaryTop>
                    <S.IconBox $tone="red">
                      <FaRoute />
                    </S.IconBox>
                    <S.Change $positive={data.summary.itinerariesChange >= 0}>
                      {changeLabel(data.summary.itinerariesChange)}
                    </S.Change>
                  </S.SummaryTop>
                  <strong>
                    {formatNumber(data.summary.publishedItineraries)}
                  </strong>
                  <span>Roteiros publicados</span>
                  <small>
                    {formatNumber(data.summary.newItineraries)} novos no período
                  </small>
                </S.SummaryCard>

                <S.SummaryCard>
                  <S.SummaryTop>
                    <S.IconBox $tone="green">
                      <FaMapMarkerAlt />
                    </S.IconBox>
                  </S.SummaryTop>
                  <strong>
                    {formatNumber(data.summary.uniqueDestinations)}
                  </strong>
                  <span>Destinos únicos</span>
                  <small>
                    {formatNumber(data.summary.totalCityEntries)} ocorrências em
                    roteiros
                  </small>
                </S.SummaryCard>

                <S.SummaryCard>
                  <S.SummaryTop>
                    <S.IconBox $tone="purple">
                      <FaHeart />
                    </S.IconBox>
                  </S.SummaryTop>
                  <strong>
                    {formatNumber(
                      data.summary.totalRatings + data.summary.totalFavorites
                    )}
                  </strong>
                  <span>Interações registradas</span>
                  <small>avaliações + favoritos</small>
                </S.SummaryCard>
              </S.SummaryGrid>

              <S.DashboardGrid>
                <S.Panel $wide>
                  <S.PanelHeader>
                    <div>
                      <h2>Crescimento da plataforma</h2>
                      <p>
                        Novos usuários e roteiros criados nos últimos 6 meses
                      </p>
                    </div>
                    <S.Legend>
                      <span>
                        <i className="users" /> Usuários
                      </span>
                      <span>
                        <i className="routes" /> Roteiros
                      </span>
                    </S.Legend>
                  </S.PanelHeader>

                  <S.GrowthChart>
                    {data.growth.map((item) => (
                      <S.GrowthColumn key={item.month}>
                        <S.Bars>
                          <S.Bar
                            $kind="users"
                            $height={(item.users / maxGrowth) * 100}
                            title={`${item.users} usuários`}
                          />
                          <S.Bar
                            $kind="routes"
                            $height={(item.itineraries / maxGrowth) * 100}
                            title={`${item.itineraries} roteiros`}
                          />
                        </S.Bars>
                        <span>{item.label}</span>
                      </S.GrowthColumn>
                    ))}
                  </S.GrowthChart>
                </S.Panel>

                <S.Panel>
                  <S.PanelHeader>
                    <div>
                      <h2>Status dos roteiros</h2>
                      <p>Publicado x planejado</p>
                    </div>
                  </S.PanelHeader>

                  <S.DonutArea>
                    <S.Donut $published={publishedPercent}>
                      <div>
                        <strong>{publishedPercent}%</strong>
                        <span>publicados</span>
                      </div>
                    </S.Donut>
                    <S.DonutLegend>
                      <div>
                        <span className="published" />
                        <p>
                          <strong>
                            {formatNumber(data.content.published)}
                          </strong>
                          Publicados
                        </p>
                      </div>
                      <div>
                        <span className="planned" />
                        <p>
                          <strong>{formatNumber(data.content.planned)}</strong>
                          Planejados
                        </p>
                      </div>
                    </S.DonutLegend>
                  </S.DonutArea>
                </S.Panel>

                <S.Panel>
                  <S.PanelHeader>
                    <div>
                      <h2>Engajamento da comunidade</h2>
                      <p>Sinais já disponíveis no banco</p>
                    </div>
                  </S.PanelHeader>

                  <S.EngagementGrid>
                    <S.EngagementItem>
                      <FaStar />
                      <div>
                        <strong>
                          {data.engagement.averageRating.toFixed(1)}
                        </strong>
                        <span>Nota média</span>
                      </div>
                    </S.EngagementItem>
                    <S.EngagementItem>
                      <FaStar />
                      <div>
                        <strong>{formatNumber(data.engagement.ratings)}</strong>
                        <span>Avaliações</span>
                      </div>
                    </S.EngagementItem>
                    <S.EngagementItem>
                      <FaHeart />
                      <div>
                        <strong>
                          {formatNumber(data.engagement.favorites)}
                        </strong>
                        <span>Favoritos</span>
                      </div>
                    </S.EngagementItem>
                    <S.EngagementItem>
                      <FaUserFriends />
                      <div>
                        <strong>
                          {formatNumber(data.engagement.connections)}
                        </strong>
                        <span>Conexões</span>
                      </div>
                    </S.EngagementItem>
                  </S.EngagementGrid>
                </S.Panel>

                <S.Panel>
                  <S.PanelHeader>
                    <div>
                      <h2>Destinos mais presentes</h2>
                      <p>Quantidade de roteiros publicados</p>
                    </div>
                  </S.PanelHeader>

                  <S.RankingList>
                    {data.topDestinations.length ? (
                      data.topDestinations.map((destination, index) => (
                        <S.RankingItem key={destination.name}>
                          <b>{index + 1}</b>
                          <span>{destination.name}</span>
                          <strong>
                            {formatNumber(destination.itineraries)} roteiros
                          </strong>
                        </S.RankingItem>
                      ))
                    ) : (
                      <S.EmptyText>
                        Ainda não há destinos suficientes.
                      </S.EmptyText>
                    )}
                  </S.RankingList>
                </S.Panel>

                <S.Panel $wide>
                  <S.PanelHeader>
                    <div>
                      <h2>Atividade recente</h2>
                      <p>Últimos cadastros e roteiros publicados</p>
                    </div>
                  </S.PanelHeader>

                  <S.ActivityList>
                    {data.recentActivity.map((activity) => (
                      <S.ActivityItem key={activity.id}>
                        <S.ActivityIcon $type={activity.type}>
                          {activity.type === 'user' ? <FaUser /> : <FaRoute />}
                        </S.ActivityIcon>
                        <div>
                          <strong>{activity.title}</strong>
                          <span>{activity.detail}</span>
                        </div>
                        <small>
                          <FaClock />
                          {relativeTime(activity.createdAt)}
                        </small>
                      </S.ActivityItem>
                    ))}
                  </S.ActivityList>
                </S.Panel>
              </S.DashboardGrid>

              <S.FutureSection>
                <S.FutureHeader>
                  <div>
                    <S.TopbarEyebrow>Inteligência do produto</S.TopbarEyebrow>
                    <h2>Indicadores estratégicos</h2>
                    <p>
                      As métricas de audiência, busca e cliques passam a ser
                      coletadas a partir desta versão. Não há dados retroativos.
                    </p>
                  </div>
                </S.FutureHeader>

                <S.FutureGrid>
                  <S.FutureCard>
                    <FaUsers />
                    <strong>{data.strategy.publisherRate}% publicam</strong>
                    <span>
                      {formatNumber(data.strategy.publishingUsers)} usuários já
                      publicaram ao menos um roteiro.
                    </span>
                  </S.FutureCard>
                  <S.FutureCard>
                    <FaMapMarkerAlt />
                    <strong>
                      {data.strategy.averageCitiesPerItinerary} cidades/roteiro
                    </strong>
                    <span>Média de destinos cadastrados em cada roteiro.</span>
                  </S.FutureCard>
                  <S.FutureCard>
                    <FaCompass />
                    <strong>
                      {formatNumber(data.strategy.inspirations)} inspirações
                    </strong>
                    <span>
                      {formatNumber(data.strategy.inspirationSources)} roteiros
                      já serviram de inspiração para outros viajantes.
                    </span>
                  </S.FutureCard>
                  <S.FutureCard>
                    <FaRoute />
                    <strong>
                      {data.strategy.plannedToRealizedRate}% de conversão
                    </strong>
                    <span>
                      {formatNumber(data.strategy.realizedTransitions)} de{' '}
                      {formatNumber(data.strategy.plannedCreated)} roteiros
                      planejados passaram para realizado desde a instrumentação.
                    </span>
                  </S.FutureCard>
                </S.FutureGrid>
              </S.FutureSection>

              <S.FutureSection>
                <S.FutureHeader>
                  <div>
                    <S.TopbarEyebrow>Audiência e retenção</S.TopbarEyebrow>
                    <h2>Uso real da plataforma</h2>
                    <p>
                      {data.audience.analyticsSince
                        ? `Coleta iniciada em ${new Date(
                            data.audience.analyticsSince
                          ).toLocaleDateString('pt-BR')}.`
                        : 'A coleta de comportamento começa agora.'}
                    </p>
                  </div>
                </S.FutureHeader>

                <S.FutureGrid>
                  <S.FutureCard>
                    <FaEye />
                    <strong>
                      {formatNumber(data.audience.pageViews)} visualizações
                    </strong>
                    <span>
                      {formatNumber(data.audience.sessions)} sessões e{' '}
                      {formatNumber(data.audience.activeUsers)} usuários
                      autenticados ativos no período.
                    </span>
                  </S.FutureCard>
                  <S.FutureCard>
                    <FaUsers />
                    <strong>Retenção D7: {data.retention.day7.rate}%</strong>
                    <span>
                      {formatNumber(data.retention.day7.retained)} de{' '}
                      {formatNumber(data.retention.day7.eligible)} usuários
                      elegíveis retornaram até o 7º dia.
                    </span>
                  </S.FutureCard>
                  <S.FutureCard>
                    <FaUsers />
                    <strong>Retenção D30: {data.retention.day30.rate}%</strong>
                    <span>
                      {formatNumber(data.retention.day30.retained)} de{' '}
                      {formatNumber(data.retention.day30.eligible)} usuários
                      elegíveis retornaram até o 30º dia.
                    </span>
                  </S.FutureCard>
                  <S.FutureCard>
                    <FaShieldAlt />
                    <strong>
                      {formatNumber(
                        data.moderation.suspended + data.moderation.banned
                      )}{' '}
                      restrições
                    </strong>
                    <span>
                      {formatNumber(data.moderation.verified)} perfis
                      verificados · {formatNumber(data.moderation.suspended)}{' '}
                      suspensos · {formatNumber(data.moderation.banned)}{' '}
                      bloqueados.
                    </span>
                  </S.FutureCard>
                </S.FutureGrid>
              </S.FutureSection>

              <S.FutureSection>
                <S.FutureHeader>
                  <div>
                    <S.TopbarEyebrow>Funil e intenção</S.TopbarEyebrow>
                    <h2>Descoberta → roteiro → parceiro</h2>
                    <p>
                      Ajuda a entender onde o usuário avança ou abandona a
                      jornada dentro da RedSterna.
                    </p>
                  </div>
                </S.FutureHeader>

                <S.FutureGrid>
                  <S.FutureCard>
                    <FaSearch />
                    <strong>
                      {formatNumber(data.funnel.discoveryViews)} descobertas
                    </strong>
                    <span>
                      Entradas em páginas de destinos ou roteiros no período.
                    </span>
                  </S.FutureCard>
                  <S.FutureCard>
                    <FaRoute />
                    <strong>
                      {formatNumber(data.funnel.itineraryViews)} roteiros
                      abertos
                    </strong>
                    <span>
                      Conversão descoberta → roteiro:{' '}
                      {data.funnel.discoveryToItineraryRate}%.
                    </span>
                  </S.FutureCard>
                  <S.FutureCard>
                    <FaCompass />
                    <strong>
                      {formatNumber(data.funnel.commercialClicks)} cliques
                      comerciais
                    </strong>
                    <span>
                      Conversão roteiro → parceiro:{' '}
                      {data.funnel.itineraryToCommercialRate}%.
                    </span>
                  </S.FutureCard>
                  <S.FutureCard>
                    <FaHeart />
                    <strong>
                      {data.strategy.inspirationSourceRate}% inspiram outros
                    </strong>
                    <span>
                      Percentual de roteiros publicados que já originaram ao
                      menos uma nova jornada.
                    </span>
                  </S.FutureCard>
                </S.FutureGrid>
              </S.FutureSection>

              <S.DashboardGrid>
                <S.Panel>
                  <S.PanelHeader>
                    <div>
                      <h2>Páginas mais vistas</h2>
                      <p>Audiência registrada no período selecionado</p>
                    </div>
                  </S.PanelHeader>
                  <S.RankingList>
                    {data.audienceBreakdown.topPages.length ? (
                      data.audienceBreakdown.topPages.map((item, index) => (
                        <S.RankingItem key={`${item.path}-page-${index}`}>
                          <b>{index + 1}</b>
                          <span>{item.path}</span>
                          <strong>{item.views} views</strong>
                        </S.RankingItem>
                      ))
                    ) : (
                      <S.EmptyText>
                        Coleta de páginas iniciada agora.
                      </S.EmptyText>
                    )}
                  </S.RankingList>
                </S.Panel>

                <S.Panel>
                  <S.PanelHeader>
                    <div>
                      <h2>Origem do tráfego</h2>
                      <p>Referências informadas pelo navegador</p>
                    </div>
                  </S.PanelHeader>
                  <S.RankingList>
                    {data.audienceBreakdown.trafficSources.length ? (
                      data.audienceBreakdown.trafficSources.map(
                        (item, index) => (
                          <S.RankingItem key={`${item.source}-source-${index}`}>
                            <b>{index + 1}</b>
                            <span>{item.source}</span>
                            <strong>{item.views} views</strong>
                          </S.RankingItem>
                        )
                      )
                    ) : (
                      <S.EmptyText>
                        As próximas visitas começarão a formar este ranking.
                      </S.EmptyText>
                    )}
                  </S.RankingList>
                </S.Panel>

                <S.Panel>
                  <S.PanelHeader>
                    <div>
                      <h2>Dispositivos</h2>
                      <p>Desktop x mobile</p>
                    </div>
                  </S.PanelHeader>
                  <S.RankingList>
                    {data.audienceBreakdown.devices.length ? (
                      data.audienceBreakdown.devices.map((item, index) => (
                        <S.RankingItem key={`${item.device}-device-${index}`}>
                          <b>{index + 1}</b>
                          <span>{item.device}</span>
                          <strong>{item.views} views</strong>
                        </S.RankingItem>
                      ))
                    ) : (
                      <S.EmptyText>Ainda sem dados de dispositivo.</S.EmptyText>
                    )}
                  </S.RankingList>
                </S.Panel>

                <S.Panel>
                  <S.PanelHeader>
                    <div>
                      <h2>País dos usuários logados</h2>
                      <p>Baseado no país informado no perfil</p>
                    </div>
                  </S.PanelHeader>
                  <S.RankingList>
                    {data.audienceBreakdown.countries.length ? (
                      data.audienceBreakdown.countries.map((item, index) => (
                        <S.RankingItem key={`${item.country}-country-${index}`}>
                          <b>{index + 1}</b>
                          <span>{item.country}</span>
                          <strong>{item.views} views</strong>
                        </S.RankingItem>
                      ))
                    ) : (
                      <S.EmptyText>Ainda sem dados suficientes.</S.EmptyText>
                    )}
                  </S.RankingList>
                </S.Panel>
              </S.DashboardGrid>

              <S.DashboardGrid>
                <S.Panel>
                  <S.PanelHeader>
                    <div>
                      <h2>Buscas mais realizadas</h2>
                      <p>Termos pesquisados dentro da RedSterna</p>
                    </div>
                  </S.PanelHeader>
                  <S.RankingList>
                    {data.searches.length ? (
                      data.searches.map((item, index) => (
                        <S.RankingItem key={`${item.term}-${index}`}>
                          <b>{index + 1}</b>
                          <span>{item.term}</span>
                          <strong>
                            {item.searches} buscas · {item.noResult} sem
                            resultado
                          </strong>
                        </S.RankingItem>
                      ))
                    ) : (
                      <S.EmptyText>
                        Coleta de buscas iniciada agora.
                      </S.EmptyText>
                    )}
                  </S.RankingList>
                </S.Panel>

                <S.Panel>
                  <S.PanelHeader>
                    <div>
                      <h2>Alta procura, pouco conteúdo</h2>
                      <p>
                        Oportunidades de destinos para estimular a comunidade
                      </p>
                    </div>
                  </S.PanelHeader>
                  <S.RankingList>
                    {data.demandGaps.length ? (
                      data.demandGaps.map((item, index) => (
                        <S.RankingItem key={`${item.term}-gap-${index}`}>
                          <b>{index + 1}</b>
                          <span>{item.term}</span>
                          <strong>
                            {item.searches} buscas · {item.contentCount}{' '}
                            roteiros
                          </strong>
                        </S.RankingItem>
                      ))
                    ) : (
                      <S.EmptyText>
                        Ainda não há volume suficiente para identificar lacunas.
                      </S.EmptyText>
                    )}
                  </S.RankingList>
                </S.Panel>

                <S.Panel $wide>
                  <S.PanelHeader>
                    <div>
                      <h2>Cliques por parceiro</h2>
                      <p>
                        Performance comercial desde o início da instrumentação
                      </p>
                    </div>
                  </S.PanelHeader>
                  <S.RankingList>
                    {data.commercial.length ? (
                      data.commercial.map((item, index) => (
                        <S.RankingItem key={`${item.partner}-${index}`}>
                          <b>{index + 1}</b>
                          <span>{item.partner}</span>
                          <strong>
                            {item.clicks} cliques · {item.ctr}% CTR
                          </strong>
                        </S.RankingItem>
                      ))
                    ) : (
                      <S.EmptyText>
                        Os próximos cliques em parceiros começarão a aparecer
                        aqui.
                      </S.EmptyText>
                    )}
                  </S.RankingList>
                </S.Panel>
              </S.DashboardGrid>
            </>
          )}
        </S.Content>
      </S.Main>
    </S.Page>
  )
}

export default Admin
